/**
 * Iter208.77 — Public "Casa Vicina" Site (Agenzia Immobiliare, playful/warm).
 *
 * Serve 4 pagine con lo stesso header (menu overlay full-screen), footer con
 * foot-word e palette coral/leaf/sun/ink:
 *   - /site/:slug                        → Home (hero collage + carousel + values + stats + about + testimonial + cta)
 *   - /site/:slug/annunci                → Griglia annunci con filtri
 *   - /site/:slug/annunci/:pid           → Dettaglio annuncio + form contatto
 *   - /site/:slug/chi-siamo              → Storia + team + valori
 *   - /site/:slug/contatti               → Form contatto + recapiti + mappa
 *
 * Il palette-widget del template originale è rimosso.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSiteLocation, useSiteNavigate, SitePreviewLink as Link } from "@/lib/previewNav";
import { Editable, Removable } from "@/lib/siteEditing";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CV_CSS = `
.casa-vicina-site {
  --paper:#FFFFFF;--surface:#F1F4F2;--ink:#132A2A;
  --coral:#FF6B4A;--coral-dark:#E14F30;--leaf:#2F6B4F;--sun:#FFC94A;
  --line:#DFE6E2;--muted:#5B6B67;
  margin:0;background:var(--paper);color:var(--ink);
  font-family:'Work Sans', -apple-system, sans-serif;
  -webkit-font-smoothing:antialiased;
}
.casa-vicina-site *{box-sizing:border-box;}
.casa-vicina-site h1,.casa-vicina-site h2,.casa-vicina-site h3{font-family:'Bricolage Grotesque','Work Sans',sans-serif;font-weight:800;color:var(--ink);text-wrap:balance;margin:0;letter-spacing:-0.01em;}
.casa-vicina-site .mono{font-family:'Space Mono',monospace;font-variant-numeric:tabular-nums;}
.casa-vicina-site .eyebrow{font-family:'Space Mono';font-size:0.72rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--leaf);}
.casa-vicina-site a{color:inherit;}
.casa-vicina-site img{max-width:100%;display:block;}
.casa-vicina-site .wrap{max-width:1220px;margin:0 auto;padding:0 32px;}
.casa-vicina-site section{position:relative;}
.casa-vicina-site header{position:sticky;top:0;z-index:80;background:var(--paper);border-bottom:2px solid var(--ink);}
.casa-vicina-site .nav{display:flex;align-items:center;justify-content:space-between;padding:16px 32px;max-width:1220px;margin:0 auto;}
.casa-vicina-site .logo{font-family:'Bricolage Grotesque';font-size:1.32rem;font-weight:800;display:flex;align-items:center;gap:8px;text-decoration:none;color:var(--ink);}
.casa-vicina-site .logo .dot{width:9px;height:9px;background:var(--coral);border-radius:50%;display:inline-block;}
.casa-vicina-site .nav-right{display:flex;align-items:center;gap:14px;}
.casa-vicina-site .btn{display:inline-flex;align-items:center;gap:8px;background:var(--coral);color:#fff;padding:12px 22px;font-weight:600;font-size:0.88rem;text-decoration:none;border:2px solid var(--ink);box-shadow:4px 4px 0 var(--ink);transition:transform .15s ease, box-shadow .15s ease;cursor:pointer;}
.casa-vicina-site .btn:hover{transform:translate(2px,2px);box-shadow:2px 2px 0 var(--ink);}
.casa-vicina-site .btn-outline{background:var(--paper);color:var(--ink);}
.casa-vicina-site .menu-toggle{width:52px;height:44px;border:2px solid var(--ink);background:var(--sun);cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;padding:0;}
.casa-vicina-site .menu-toggle span{width:22px;height:2px;background:var(--ink);display:block;transition:transform .25s ease, opacity .25s ease;}
.casa-vicina-site .menu-toggle.open span:nth-child(1){transform:translateY(7px) rotate(45deg);}
.casa-vicina-site .menu-toggle.open span:nth-child(2){opacity:0;}
.casa-vicina-site .menu-toggle.open span:nth-child(3){transform:translateY(-7px) rotate(-45deg);}
.casa-vicina-site .overlay-nav{position:fixed;inset:0;background:var(--leaf);z-index:70;display:flex;flex-direction:column;justify-content:center;padding:80px 60px;transform:translateY(-100%);transition:transform .45s cubic-bezier(.7,0,.2,1);}
.casa-vicina-site .overlay-nav.open{transform:translateY(0);}
.casa-vicina-site .overlay-nav a{font-family:'Bricolage Grotesque';font-size:clamp(2.2rem,6vw,4rem);font-weight:800;color:#fff;text-decoration:none;display:block;padding:10px 0;border-bottom:2px solid rgba(255,255,255,0.18);max-width:640px;}
.casa-vicina-site .overlay-nav a:hover{color:var(--sun);}
.casa-vicina-site .overlay-foot{margin-top:40px;display:flex;gap:40px;font-family:'Space Mono';font-size:0.85rem;color:#CFE6D8;flex-wrap:wrap;}
.casa-vicina-site .hero{padding:64px 0 0;}
.casa-vicina-site .hero-head{max-width:800px;}
.casa-vicina-site .hero-head h1{font-size:clamp(2.4rem,5.2vw,4.1rem);line-height:1.02;}
.casa-vicina-site .hero-head p{font-size:1.08rem;line-height:1.6;color:var(--muted);max-width:520px;margin:20px 0 0;}
.casa-vicina-site .collage{display:grid;grid-template-columns:1.1fr 0.8fr 1fr;gap:20px;margin-top:44px;align-items:end;position:relative;}
.casa-vicina-site .collage .ph{border:2px solid var(--ink);overflow:hidden;position:relative;background:var(--surface);}
.casa-vicina-site .collage .ph svg,.casa-vicina-site .collage .ph img{width:100%;height:100%;display:block;object-fit:cover;}
.casa-vicina-site .collage .ph.a{height:300px;}
.casa-vicina-site .collage .ph.b{height:220px;}
.casa-vicina-site .collage .ph.c{height:360px;}
.casa-vicina-site .price-sticker{position:absolute;top:-16px;right:-14px;background:var(--sun);color:var(--ink);padding:12px 16px;border:2px solid var(--ink);transform:rotate(-6deg);box-shadow:5px 5px 0 var(--ink);z-index:5;}
.casa-vicina-site .price-sticker .amt{font-family:'Bricolage Grotesque';font-weight:800;font-size:1.15rem;}
.casa-vicina-site .price-sticker .lbl{font-family:'Space Mono';font-size:0.6rem;text-transform:uppercase;}
.casa-vicina-site .searchbar{margin-top:-1px;background:var(--ink);padding:20px 32px;display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:22px;align-items:end;}
.casa-vicina-site .searchbar .field label{display:block;font-family:'Space Mono';font-size:0.62rem;letter-spacing:0.08em;text-transform:uppercase;color:#9FB6AE;margin-bottom:6px;}
.casa-vicina-site .searchbar select,.casa-vicina-site .searchbar input{width:100%;background:transparent;border:none;border-bottom:2px solid #3E5C54;color:#fff;padding:6px 0;font-family:'Work Sans';font-weight:500;outline:none;}
.casa-vicina-site .searchbar .btn{white-space:nowrap;}
.casa-vicina-site .sec-head{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:36px;}
.casa-vicina-site .sec-head h2{font-size:clamp(1.7rem,2.6vw,2.3rem);}
.casa-vicina-site .sec-head .view-all{font-family:'Space Mono';font-size:0.82rem;text-decoration:none;color:var(--coral-dark);font-weight:700;white-space:nowrap;}
.casa-vicina-site .listings{padding:96px 0 90px;}
.casa-vicina-site .carousel{display:flex;gap:24px;overflow-x:auto;scroll-snap-type:x mandatory;padding-bottom:14px;margin:0 -32px;padding-left:32px;padding-right:32px;}
.casa-vicina-site .carousel::-webkit-scrollbar{height:6px;}
.casa-vicina-site .carousel::-webkit-scrollbar-thumb{background:var(--line);}
.casa-vicina-site .card{flex:0 0 320px;scroll-snap-align:start;background:var(--paper);border:2px solid var(--ink);box-shadow:6px 6px 0 var(--surface);text-decoration:none;color:var(--ink);display:block;transition:transform .15s ease, box-shadow .15s ease;}
.casa-vicina-site .card:hover{transform:translate(2px,2px);box-shadow:4px 4px 0 var(--surface);}
.casa-vicina-site .card-media{position:relative;aspect-ratio:4/3;border-bottom:2px solid var(--ink);overflow:hidden;}
.casa-vicina-site .card-media svg,.casa-vicina-site .card-media img{width:100%;height:100%;object-fit:cover;}
.casa-vicina-site .tag{position:absolute;top:12px;left:12px;background:var(--sun);color:var(--ink);font-family:'Space Mono';font-size:0.62rem;letter-spacing:0.06em;text-transform:uppercase;padding:5px 10px;border:2px solid var(--ink);}
.casa-vicina-site .fav{position:absolute;top:10px;right:10px;width:34px;height:34px;background:var(--paper);border:2px solid var(--ink);display:flex;align-items:center;justify-content:center;}
.casa-vicina-site .fav svg{width:16px;height:16px;stroke:var(--ink);fill:none;}
.casa-vicina-site .card-body{padding:20px;}
.casa-vicina-site .card-price{font-family:'Bricolage Grotesque';font-size:1.28rem;font-weight:800;color:var(--coral-dark);}
.casa-vicina-site .card-title{font-size:1.03rem;font-weight:700;margin:8px 0 4px;}
.casa-vicina-site .card-loc{font-size:0.85rem;color:var(--muted);margin-bottom:14px;}
.casa-vicina-site .card-meta{display:flex;gap:14px;border-top:2px dashed var(--line);padding-top:12px;}
.casa-vicina-site .card-meta span{display:flex;align-items:center;gap:5px;font-family:'Space Mono';font-size:0.74rem;}
.casa-vicina-site .card-meta svg{width:13px;height:13px;stroke:var(--leaf);fill:none;}
.casa-vicina-site .scroll-hint{display:flex;gap:8px;margin-top:18px;font-family:'Space Mono';font-size:0.72rem;color:var(--muted);align-items:center;}
.casa-vicina-site .scroll-hint svg{width:16px;height:16px;stroke:var(--muted);fill:none;}
.casa-vicina-site .values{background:var(--ink);color:#fff;padding:100px 0;}
.casa-vicina-site .values .eyebrow{color:var(--sun);}
.casa-vicina-site .value-row{display:grid;grid-template-columns:60px 1fr 1.3fr;gap:32px;align-items:center;padding:30px 0;border-top:1px solid #2C4F4F;}
.casa-vicina-site .values-list{margin-top:20px;border-bottom:1px solid #2C4F4F;}
.casa-vicina-site .value-row .icon-wrap{width:44px;height:44px;background:var(--coral);border:2px solid var(--ink);display:flex;align-items:center;justify-content:center;}
.casa-vicina-site .value-row .icon-wrap svg{width:22px;height:22px;stroke:#fff;fill:none;}
.casa-vicina-site .value-row h3{color:#fff;font-size:1.25rem;}
.casa-vicina-site .value-row p{font-size:0.94rem;line-height:1.6;color:#B9CBC4;margin:0;}
.casa-vicina-site .stats{padding:0;margin:80px 0;}
.casa-vicina-site .stats-band{background:var(--sun);transform:skewY(-2deg);padding:56px 0;border-top:2px solid var(--ink);border-bottom:2px solid var(--ink);}
.casa-vicina-site .stats-band .wrap{transform:skewY(2deg);}
.casa-vicina-site .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);text-align:center;}
.casa-vicina-site .stat .num{font-family:'Bricolage Grotesque';font-weight:800;font-size:clamp(2rem,3.4vw,2.6rem);}
.casa-vicina-site .stat .lbl{font-family:'Space Mono';font-size:0.68rem;letter-spacing:0.06em;text-transform:uppercase;margin-top:6px;}
.casa-vicina-site .about-preview,.casa-vicina-site .about-full{padding:70px 0 100px;}
.casa-vicina-site .about-copy{max-width:760px;position:relative;}
.casa-vicina-site .about-copy p{font-size:1.28rem;line-height:1.65;font-family:'Bricolage Grotesque';font-weight:500;color:var(--ink);}
.casa-vicina-site .about-inset{float:right;width:220px;height:170px;border:2px solid var(--ink);margin:6px 0 20px 28px;overflow:hidden;background:var(--coral);}
.casa-vicina-site .about-inset svg{width:100%;height:100%;}
.casa-vicina-site .about-copy .link-arrow{display:inline-flex;margin-top:18px;align-items:center;gap:8px;text-decoration:none;color:var(--ink);font-weight:700;border-bottom:3px solid var(--sun);padding-bottom:2px;clear:both;}
.casa-vicina-site .testimonial{padding:40px 0 100px;}
.casa-vicina-site .test-box{max-width:760px;margin-left:8%;position:relative;}
.casa-vicina-site .test-mark{font-family:'Bricolage Grotesque';font-size:6rem;color:var(--sun);line-height:1;position:absolute;top:-40px;left:-40px;z-index:0;}
.casa-vicina-site .testimonial blockquote{position:relative;z-index:1;font-family:'Bricolage Grotesque';font-weight:600;font-size:clamp(1.3rem,2.2vw,1.8rem);line-height:1.45;margin:0;color:var(--ink);}
.casa-vicina-site .testimonial cite{display:block;margin-top:22px;font-family:'Space Mono';font-style:normal;font-size:0.8rem;color:var(--muted);}
.casa-vicina-site .cta{background:var(--leaf);}
.casa-vicina-site .cta .wrap{display:flex;align-items:center;justify-content:space-between;gap:32px;padding:70px 32px;flex-wrap:wrap;}
.casa-vicina-site .cta h2{color:#fff;font-size:clamp(1.7rem,2.8vw,2.3rem);max-width:440px;}
.casa-vicina-site .cta .btn{background:var(--sun);color:var(--ink);flex-shrink:0;}
.casa-vicina-site footer{background:var(--paper);color:var(--ink);padding:70px 0 0;}
.casa-vicina-site .foot-top{display:grid;grid-template-columns:1fr 1fr;gap:40px;padding-bottom:50px;}
.casa-vicina-site .foot-top h4{font-family:'Space Mono';font-size:0.7rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);margin-bottom:16px;}
.casa-vicina-site .foot-links{display:flex;flex-wrap:wrap;gap:10px 28px;}
.casa-vicina-site .foot-links a{text-decoration:none;color:var(--ink);font-size:0.95rem;font-weight:500;}
.casa-vicina-site .foot-links a:hover{color:var(--coral-dark);}
.casa-vicina-site .foot-contact p{margin:0 0 8px;font-size:0.95rem;}
.casa-vicina-site .foot-word{font-family:'Bricolage Grotesque';font-weight:800;font-size:clamp(3rem,12vw,9rem);line-height:0.85;border-top:2px solid var(--ink);padding:20px 32px 10px;letter-spacing:-0.02em;color:var(--ink);max-width:1220px;margin:0 auto;}
.casa-vicina-site .foot-bottom{display:flex;justify-content:space-between;padding:18px 32px 24px;max-width:1220px;margin:0 auto;font-size:0.76rem;font-family:'Space Mono';color:var(--muted);border-top:1px solid var(--line);}
/* Pagine aggiuntive */
.casa-vicina-site .contact-page,.casa-vicina-site .listing-detail-page{padding:64px 0 100px;}
.casa-vicina-site .contact-grid{display:grid;grid-template-columns:1.1fr 0.9fr;gap:56px;align-items:flex-start;}
.casa-vicina-site .field-row{margin-bottom:18px;}
.casa-vicina-site .field-row label{display:block;font-family:'Space Mono';font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);margin-bottom:8px;}
.casa-vicina-site .field-row input,.casa-vicina-site .field-row textarea{width:100%;background:var(--paper);border:2px solid var(--ink);padding:12px 14px;font-family:'Work Sans';font-size:0.95rem;color:var(--ink);outline:none;}
.casa-vicina-site .field-row input:focus,.casa-vicina-site .field-row textarea:focus{background:#FFFEF3;}
.casa-vicina-site .field-row textarea{min-height:130px;resize:vertical;}
.casa-vicina-site .contact-side{background:var(--sun);border:2px solid var(--ink);box-shadow:6px 6px 0 var(--ink);padding:32px 28px;}
.casa-vicina-site .contact-side h3{margin-bottom:14px;font-size:1.2rem;}
.casa-vicina-site .contact-side .row{display:flex;gap:10px;align-items:flex-start;margin:14px 0;font-size:0.95rem;color:var(--ink);}
.casa-vicina-site .contact-side .row svg{width:16px;height:16px;stroke:var(--ink);fill:none;flex-shrink:0;margin-top:2px;}
.casa-vicina-site .contact-map{margin-top:24px;aspect-ratio:16/10;background:var(--paper);border:2px solid var(--ink);overflow:hidden;}
.casa-vicina-site .contact-map iframe{width:100%;height:100%;border:none;}
.casa-vicina-site .filters-bar{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:32px;padding:18px;background:var(--surface);border:2px solid var(--ink);}
.casa-vicina-site .filters-bar select,.casa-vicina-site .filters-bar input{background:var(--paper);border:2px solid var(--ink);color:var(--ink);padding:10px 12px;font-family:'Work Sans';font-size:0.9rem;outline:none;}
.casa-vicina-site .grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:28px;}
.casa-vicina-site .grid-3 .card{flex:none;}
.casa-vicina-site .listing-hero{aspect-ratio:16/9;background:var(--surface);border:2px solid var(--ink);overflow:hidden;margin-bottom:32px;}
.casa-vicina-site .listing-hero svg,.casa-vicina-site .listing-hero img{width:100%;height:100%;object-fit:cover;}
.casa-vicina-site .listing-detail-grid{display:grid;grid-template-columns:1.4fr 1fr;gap:48px;}
.casa-vicina-site .listing-info-panel{background:var(--sun);border:2px solid var(--ink);box-shadow:6px 6px 0 var(--ink);padding:28px;position:sticky;top:100px;}
.casa-vicina-site .listing-info-panel .price{font-family:'Bricolage Grotesque';font-weight:800;font-size:1.9rem;color:var(--coral-dark);margin-bottom:8px;}
.casa-vicina-site .listing-info-panel .stats-list{display:grid;grid-template-columns:1fr 1fr;gap:12px 24px;margin:20px 0 24px;padding:16px 0;border-top:2px dashed var(--ink);border-bottom:2px dashed var(--ink);font-family:'Space Mono';font-size:0.82rem;color:var(--ink);}
.casa-vicina-site .listing-info-panel .stats-list .k{font-size:0.68rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);display:block;margin-bottom:4px;}
.casa-vicina-site .back-link{display:inline-flex;align-items:center;gap:8px;font-family:'Space Mono';font-size:0.82rem;color:var(--coral-dark);text-decoration:none;margin-bottom:20px;font-weight:700;}
.casa-vicina-site .empty-state{padding:80px 20px;text-align:center;color:var(--muted);font-family:'Space Mono';font-size:0.9rem;}
.casa-vicina-site .team-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px;margin-top:44px;}
.casa-vicina-site .team-card{background:var(--paper);border:2px solid var(--ink);box-shadow:6px 6px 0 var(--surface);padding:24px;}
.casa-vicina-site .team-card .avatar{aspect-ratio:1;background:var(--coral);margin-bottom:16px;border:2px solid var(--ink);display:flex;align-items:center;justify-content:center;font-family:'Bricolage Grotesque';font-size:2.5rem;color:#fff;font-weight:800;}
.casa-vicina-site .team-card h4{font-family:'Bricolage Grotesque';font-weight:800;font-size:1.15rem;color:var(--ink);margin:0 0 4px;}
.casa-vicina-site .team-card .role{font-family:'Space Mono';font-size:0.72rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);}
@media (max-width: 980px){
  .casa-vicina-site .collage{grid-template-columns:1fr 1fr;}
  .casa-vicina-site .collage .ph.c{display:none;}
  .casa-vicina-site .searchbar{grid-template-columns:1fr 1fr;row-gap:14px;}
  .casa-vicina-site .searchbar .btn{grid-column:1/-1;justify-content:center;}
  .casa-vicina-site .value-row{grid-template-columns:44px 1fr;}
  .casa-vicina-site .value-row p{grid-column:2/3;}
  .casa-vicina-site .stats-grid,.casa-vicina-site .grid-3,.casa-vicina-site .team-grid{grid-template-columns:1fr;row-gap:28px;}
  .casa-vicina-site .about-inset{float:none;width:100%;height:180px;margin:0 0 20px;}
  .casa-vicina-site .foot-top{grid-template-columns:1fr;}
  .casa-vicina-site .contact-grid,.casa-vicina-site .listing-detail-grid{grid-template-columns:1fr;gap:36px;}
  .casa-vicina-site .listing-info-panel{position:static;}
}
`;

function CollagePlaceholder({ variant = "a" }) {
  const bg = variant === "a" ? "#2F6B4F" : variant === "b" ? "#FFC94A" : "#FF6B4A";
  return (
    <svg viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="300" height="400" fill={bg} />
      <g fill="#132A2A" opacity="0.85">
        <rect x="40" y="220" width="80" height="140" />
        <rect x="140" y="180" width="120" height="180" />
      </g>
    </svg>
  );
}

function PropPlaceholder({ variant = 0 }) {
  const fills = ["#2F6B4F", "#FF6B4A", "#FFC94A", "#2F6B4F"];
  return (
    <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="400" height="300" fill="#F1F4F2" />
      <g fill={fills[variant % 4]}><path d="M50 260 L50 150 L200 70 L350 150 L350 260 Z"/></g>
      <g fill="#132A2A" opacity="0.85">
        <rect x="90" y="180" width="50" height="80" />
        <rect x="160" y="170" width="50" height="90" />
        <rect x="230" y="170" width="50" height="90" />
      </g>
    </svg>
  );
}

function CVHeader({ slug, studio }) {
  const base = `/site/${slug}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useSiteLocation();
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  return (
    <header data-testid="cv-header">
      <div className="nav">
        <Link to={base} className="logo" data-testid="cv-logo">
          <span className="dot" /> {studio.name || "Casa Vicina"}
        </Link>
        <div className="nav-right">
          <Link to={`${base}/contatti`} className="btn btn-outline" data-testid="cv-cta-header">Valuta casa tua</Link>
          <button
            className={"menu-toggle" + (menuOpen ? " open" : "")}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
            data-testid="cv-menu-toggle"
          ><span/><span/><span/></button>
        </div>
      </div>
      <nav className={"overlay-nav" + (menuOpen ? " open" : "")} data-testid="cv-overlay-nav">
        <Link to={base} data-testid="cv-nav-home">Home</Link>
        <Link to={`${base}/annunci`} data-testid="cv-nav-annunci">Annunci</Link>
        <Link to={`${base}/chi-siamo`} data-testid="cv-nav-chi-siamo">Chi siamo</Link>
        <Link to={`${base}/contatti`} data-testid="cv-nav-contatti">Contatti</Link>
        <div className="overlay-foot">
          {studio.phone && <span>{studio.phone}</span>}
          {studio.email && <span>{studio.email}</span>}
        </div>
      </nav>
    </header>
  );
}

function CVFooter({ slug, studio }) {
  const base = `/site/${slug}`;
  const year = new Date().getFullYear();
  const name = studio.name || "Casa Vicina";
  return (
    <footer data-testid="cv-footer">
      <div className="wrap">
        <div className="foot-top">
          <div>
            <h4>Naviga</h4>
            <div className="foot-links">
              <Link to={base}>Home</Link>
              <Link to={`${base}/annunci`}>Annunci</Link>
              <Link to={`${base}/chi-siamo`}>Chi siamo</Link>
              <Link to={`${base}/contatti`}>Contatti</Link>
            </div>
          </div>
          <div className="foot-contact">
            <h4>Contatti</h4>
            {studio.phone && <p>{studio.phone}</p>}
            {studio.email && <p>{studio.email}</p>}
            {studio.address && <p>{studio.address}</p>}
          </div>
        </div>
      </div>
      <div className="foot-word" data-testid="cv-foot-word">{name}</div>
      <div className="foot-bottom">
        <span>© {year} {name}</span>
        {studio.vat && <span>P.IVA {studio.vat}</span>}
      </div>
    </footer>
  );
}

function PropertyCardCV({ p, slug, index = 0 }) {
  const priceFmt = p.price ? `€ ${Number(p.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <Link to={`/site/${slug}/annunci/${p.id}`} className="card" data-testid={`cv-property-card-${p.id}`}>
      <div className="card-media">
        <span className="tag">{p.tag || "In vendita"}</span>
        <div className="fav"><svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor"><path d="M12 21s-7-4.35-9.5-9C.7 8.2 3 4.5 7 4.5c2 0 3.5 1 5 2.7 1.5-1.7 3-2.7 5-2.7 4 0 6.3 3.7 4.5 7.5-2.5 4.65-9.5 9-9.5 9z"/></svg></div>
        {p.cover_url ? <img src={p.cover_url} alt={p.address}/> : <PropPlaceholder variant={index}/>}
      </div>
      <div className="card-body">
        <div className="card-price">{priceFmt}</div>
        <h3 className="card-title">{p.address || "Immobile"}</h3>
        <div className="card-loc">{p.city}</div>
        <div className="card-meta">
          {p.surface_m2 ? <span><svg viewBox="0 0 24 24" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="1"/></svg>{p.surface_m2} mq</span> : null}
          {p.rooms ? <span><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M4 20h16M6 20V10l6-4 6 4v10"/></svg>{p.rooms} locali</span> : null}
          {p.bathrooms ? <span><svg viewBox="0 0 24 24" strokeWidth="2"><circle cx="12" cy="12" r="9"/></svg>{p.bathrooms} bagni</span> : null}
        </div>
      </div>
    </Link>
  );
}

function ContactFormCV({ slug, propertyId, testIdPrefix = "cv-contact" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nome, email e messaggio sono obbligatori."); return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/site/${slug}/contact`, { ...form, property_id: propertyId || null });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Messaggio inviato — ti risponderemo entro un giorno lavorativo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito, riprova.");
    } finally {
      setSending(false);
    }
  };
  if (sent) return (
    <div className="contact-side" data-testid={`${testIdPrefix}-success`} style={{background:"var(--leaf)",color:"#fff"}}>
      <h3 style={{color:"#fff"}}>Grazie! Messaggio ricevuto.</h3>
      <p style={{fontSize:"0.95rem",color:"#CFE6D8",lineHeight:1.6}}>Ti risponderemo entro un giorno lavorativo all'indirizzo che ci hai fornito.</p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid={`${testIdPrefix}-form`}>
      <div className="field-row"><label>Nome e cognome</label><input value={form.name} onChange={(e)=>setForm(f=>({...f, name:e.target.value}))} data-testid={`${testIdPrefix}-name`} required /></div>
      <div className="field-row"><label>Email</label><input type="email" value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} data-testid={`${testIdPrefix}-email`} required /></div>
      <div className="field-row"><label>Telefono</label><input value={form.phone} onChange={(e)=>setForm(f=>({...f, phone:e.target.value}))} data-testid={`${testIdPrefix}-phone`} /></div>
      <div className="field-row"><label>Messaggio</label><textarea value={form.message} onChange={(e)=>setForm(f=>({...f, message:e.target.value}))} data-testid={`${testIdPrefix}-message`} required /></div>
      <button type="submit" className="btn" disabled={sending} data-testid={`${testIdPrefix}-submit`}>{sending ? "Invio…" : "Invia richiesta"}</button>
    </form>
  );
}

function CVHomePage({ slug, studio, properties }) {
  const founded = studio.founded_year;
  const anni = founded ? (2026 - Number(founded)) : "—";
  const featured = properties?.[0];
  const stickerPrice = featured ? `€ ${Math.round(Number(featured.price) / 1000)}k` : "€ 340k";
  return (
    <>
      <Removable sectionKey="hero">
      <section className="hero" data-testid="cv-section-hero">
        <div className="wrap">
          <div className="hero-head">
            <span className="eyebrow">La tua agenzia di quartiere</span>
            <Editable field="cv.hero.h1" as="h1" multiline className="" style={{marginTop:"14px"}}>Trovare casa non deve essere complicato.</Editable>
            <Editable field="cv.hero.p" as="p" multiline>Ti accompagniamo passo passo, senza gergo tecnico e senza sorprese. Solo persone che conoscono ogni via del tuo quartiere.</Editable>
          </div>
          <div className="collage">
            <div className="ph a"><CollagePlaceholder variant="a"/></div>
            <div className="ph b">
              <CollagePlaceholder variant="b"/>
              <div className="price-sticker"><div className="amt">{stickerPrice}</div><div className="lbl">{featured?.rooms ? `${featured.rooms} locali` : "Trilocale"}</div></div>
            </div>
            <div className="ph c"><CollagePlaceholder variant="c"/></div>
          </div>
          <div className="searchbar" data-testid="cv-searchbar">
            <div className="field"><label>Zona</label><input placeholder={studio.city || "Bologna"} defaultValue={studio.city || ""} /></div>
            <div className="field"><label>Tipo</label>
              <select defaultValue=""><option value="">Tutti</option><option>Appartamento</option><option>Villetta</option><option>Attico</option></select>
            </div>
            <div className="field"><label>Budget max</label>
              <select defaultValue=""><option value="">Tutti</option><option>250.000€</option><option>450.000€</option><option>700.000€</option></select>
            </div>
            <Link to={`/site/${slug}/annunci`} className="btn" data-testid="cv-search-btn">Cerca</Link>
          </div>
        </div>
      </section>
      </Removable>

      {properties?.length > 0 && (
        <Removable sectionKey="listings">
        <section className="listings" data-testid="cv-section-listings">
          <div className="wrap">
            <div className="sec-head">
              <div><span className="eyebrow">Appena arrivati</span><h2 style={{marginTop:"10px"}}>Le case del momento</h2></div>
              <Link to={`/site/${slug}/annunci`} className="view-all">Vedi tutti gli annunci →</Link>
            </div>
          </div>
          <div className="carousel" data-testid="cv-carousel">
            {properties.map((p, i) => <PropertyCardCV key={p.id} p={p} slug={slug} index={i} />)}
          </div>
          <div className="wrap">
            <div className="scroll-hint"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M9 6l6 6-6 6"/></svg>Scorri per vedere altri annunci</div>
          </div>
        </section>
        </Removable>
      )}

      <Removable sectionKey="values">
      <section className="values" data-testid="cv-section-values">
        <div className="wrap">
          <span className="eyebrow">Come lavoriamo</span>
          <Editable field="cv.values.h2" as="h2" style={{marginTop:"14px",color:"#fff"}}>Semplice, umano, senza sorprese</Editable>
          <div className="values-list">
            <div className="value-row">
              <div className="icon-wrap"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M4 21c0-4 3.5-6 8-6s8 2 8 6M12 11a4 4 0 100-8 4 4 0 000 8z"/></svg></div>
              <Editable field="cv.values.a.h3" as="h3">Un consulente dedicato</Editable>
              <Editable field="cv.values.a.p" as="p" multiline>Parli sempre con la stessa persona, dalla prima chiamata al rogito.</Editable>
            </div>
            <div className="value-row">
              <div className="icon-wrap"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M12 2 2 8l10 6 10-6-10-6zM2 16l10 6 10-6M2 12l10 6 10-6"/></svg></div>
              <Editable field="cv.values.b.h3" as="h3">Zero costi nascosti</Editable>
              <Editable field="cv.values.b.p" as="p" multiline>Provvigioni chiare, spiegate prima di firmare qualsiasi cosa.</Editable>
            </div>
            <div className="value-row">
              <div className="icon-wrap"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M13 2 3 14h8l-1 8 11-14h-8z"/></svg></div>
              <Editable field="cv.values.c.h3" as="h3">Risposte rapide</Editable>
              <Editable field="cv.values.c.p" as="p" multiline>Ti rispondiamo entro un giorno lavorativo, sempre.</Editable>
            </div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="stats">
      <section className="stats" data-testid="cv-section-stats">
        <div className="stats-band">
          <div className="wrap">
            <div className="stats-grid">
              <div className="stat"><div className="num">198</div><div className="lbl">Case vendute</div></div>
              <div className="stat"><div className="num">{anni}</div><div className="lbl">Anni sul territorio</div></div>
              <div className="stat"><div className="num">4.8</div><div className="lbl">Voto clienti</div></div>
              <div className="stat"><div className="num">24h</div><div className="lbl">Tempo di risposta</div></div>
            </div>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="about-preview">
      <section className="about-preview" data-testid="cv-section-about-preview">
        <div className="wrap">
          <span className="eyebrow">Chi siamo</span>
          <div className="about-copy" style={{marginTop:"16px"}}>
            <div className="about-inset">
              <svg viewBox="0 0 220 170" preserveAspectRatio="xMidYMid slice"><rect width="220" height="170" fill="#FF6B4A"/><g fill="#132A2A"><circle cx="110" cy="80" r="40"/></g></svg>
            </div>
            <Editable field="cv.about.p" as="p" multiline>{`${studio.name || "Casa Vicina"} nasce${founded ? ` nel ${founded}` : ""}${studio.city ? ` a ${studio.city}` : ""} da chi in questo quartiere ci abita davvero. Zero call center: solo chi conosci, disponibile quando serve — dalla prima visita fino alle chiavi in mano.`}</Editable>
            <Link to={`/site/${slug}/chi-siamo`} className="link-arrow">Conosci il team →</Link>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="testimonial">
      <section className="testimonial" data-testid="cv-section-testimonial">
        <div className="wrap">
          <div className="test-box">
            <div className="test-mark">“</div>
            <Editable field="cv.testimonial.quote" as="blockquote" multiline>Il primo agente immobiliare che ci ha detto la verità sul prezzo, invece di dirci quello che volevamo sentire.</Editable>
            <Editable field="cv.testimonial.cite" as="cite">— Giulia e Marco, acquirenti in zona {studio.city || "Navile"}</Editable>
          </div>
        </div>
      </section>
      </Removable>

      <Removable sectionKey="cta">
      <section className="cta" data-testid="cv-section-cta">
        <div className="wrap">
          <Editable field="cv.cta.h2" as="h2" multiline>Vuoi sapere quanto vale casa tua? Valutazione gratuita in 48 ore.</Editable>
          <Link to={`/site/${slug}/contatti`} className="btn" data-testid="cv-cta-btn">
            <Editable field="cv.cta.btn">Richiedi la valutazione</Editable>
          </Link>
        </div>
      </section>
      </Removable>
    </>
  );
}

function CVAnnunciPage({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/site/${slug}/properties`).then((r) => setItems(r.data.properties || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  }, [slug]);
  const cities = useMemo(() => Array.from(new Set(items.map(i => i.city).filter(Boolean))), [items]);
  const filtered = useMemo(() => items.filter((p) => {
    if (city && p.city !== city) return false;
    if (search && !(p.address || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, city, search]);
  return (
    <section className="listings" data-testid="cv-page-annunci">
      <div className="wrap">
        <div className="sec-head">
          <div><span className="eyebrow">Tutti gli immobili</span><h2 style={{marginTop:"10px"}}>Il nostro catalogo</h2></div>
          <span className="view-all" data-testid="cv-annunci-count">{filtered.length} immobil{filtered.length === 1 ? "e" : "i"}</span>
        </div>
        <div className="filters-bar" data-testid="cv-annunci-filters">
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="cv-filter-city">
            <option value="">Tutte le zone</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input placeholder="Cerca per indirizzo…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cv-filter-search" style={{flex:1, minWidth:"220px"}} />
        </div>
        {loading ? <div className="empty-state">Caricamento…</div>
          : filtered.length === 0 ? <div className="empty-state" data-testid="cv-annunci-empty">Nessun immobile disponibile.</div>
          : <div className="grid-3">{filtered.map((p, i) => <PropertyCardCV key={p.id} p={p} slug={slug} index={i} />)}</div>}
      </div>
    </section>
  );
}

function CVChiSiamoPage({ slug, studio }) {
  const founded = studio.founded_year;
  const team = [
    { name: studio.name || "Casa Vicina", role: "Fondatore", initial: (studio.name || "C")[0] },
    { name: "Elena Conti", role: "Consulente", initial: "E" },
    { name: "Davide Ferri", role: "Valutatore", initial: "D" },
  ];
  return (
    <section className="about-full" data-testid="cv-page-chi-siamo">
      <div className="wrap">
        <span className="eyebrow">Chi siamo</span>
        <h2 style={{fontSize:"clamp(2rem,3.4vw,2.8rem)",marginTop:"12px",marginBottom:"24px"}}>La nostra storia</h2>
        <p style={{fontSize:"1.1rem",lineHeight:1.7,maxWidth:"760px",color:"var(--ink)",fontFamily:"'Bricolage Grotesque'",fontWeight:500}}>
          {studio.name || "Casa Vicina"} nasce{founded ? ` nel ${founded}` : ""}{studio.city ? ` a ${studio.city}` : ""} da chi in questo quartiere ci abita davvero. Sei persone, zero call center: solo chi conosci, disponibile quando serve — dalla prima visita fino alle chiavi in mano.
        </p>

        <span className="eyebrow" style={{marginTop:"48px",display:"block"}}>Il nostro team</span>
        <div className="team-grid">
          {team.map((m, i) => (
            <div className="team-card" key={i} data-testid={`cv-team-${i}`}>
              <div className="avatar">{m.initial}</div>
              <h4>{m.name}</h4>
              <div className="role">{m.role}</div>
            </div>
          ))}
        </div>

        <span className="eyebrow" style={{marginTop:"72px",display:"block"}}>I nostri valori</span>
        <div style={{background:"var(--ink)",color:"#fff",padding:"48px 32px",marginTop:"24px",border:"2px solid var(--ink)"}}>
          <div className="values-list">
            <div className="value-row">
              <div className="icon-wrap"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M12 2 3 7v13h6v-6h6v6h6V7z"/></svg></div>
              <h3>Trasparenza</h3>
              <p>Provvigioni chiare, valutazioni oneste. Sappiamo dire "no" quando il prezzo non ci convince.</p>
            </div>
            <div className="value-row">
              <div className="icon-wrap"><svg viewBox="0 0 24 24" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.5-6 8-6s8 2 8 6"/></svg></div>
              <h3>Un solo referente</h3>
              <p>Dall'inizio alla fine hai una sola persona che conosce tutto del tuo caso.</p>
            </div>
            <div className="value-row">
              <div className="icon-wrap"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M13 2 3 14h8l-1 8 11-14h-8z"/></svg></div>
              <h3>Risposte rapide</h3>
              <p>Ti richiamiamo entro un giorno lavorativo, sempre — anche per un dubbio veloce.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CVContattiPage({ slug, studio }) {
  const mapQuery = encodeURIComponent(studio.address || studio.city || "Bologna");
  return (
    <section className="contact-page" data-testid="cv-page-contatti">
      <div className="wrap">
        <span className="eyebrow">Contattaci</span>
        <h2 style={{fontSize:"clamp(2rem,3.4vw,2.8rem)",marginTop:"12px",marginBottom:"48px"}}>Ciao, come possiamo aiutarti?</h2>
        <div className="contact-grid">
          <div><ContactFormCV slug={slug} testIdPrefix="cv-contatti" /></div>
          <aside className="contact-side" data-testid="cv-contatti-side">
            <h3>Recapiti</h3>
            {studio.phone && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>{studio.phone}</div>}
            {studio.email && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M4 4h16v16H4z"/><path d="M4 4l8 8 8-8"/></svg>{studio.email}</div>}
            {studio.address && <div className="row"><svg viewBox="0 0 24 24" strokeWidth="1.6"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>{studio.address}</div>}
            <div className="contact-map" data-testid="cv-contatti-map">
              <iframe title="Mappa" src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} loading="lazy" />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function CVListingDetailPage({ slug, pid }) {
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/site/${slug}/properties/${pid}`)
      .then((r) => setProp(r.data.property)).catch(() => setProp(null)).finally(() => setLoading(false));
  }, [slug, pid]);
  if (loading) return <section className="listing-detail-page"><div className="wrap empty-state">Caricamento…</div></section>;
  if (!prop) return <section className="listing-detail-page"><div className="wrap empty-state" data-testid="cv-listing-detail-not-found">Immobile non disponibile.</div></section>;
  const priceFmt = prop.price ? `€ ${Number(prop.price).toLocaleString("it-IT")}` : "Prezzo su richiesta";
  return (
    <section className="listing-detail-page" data-testid="cv-page-listing-detail">
      <div className="wrap">
        <Link to={`/site/${slug}/annunci`} className="back-link" data-testid="cv-listing-back">← Torna agli annunci</Link>
        <div className="listing-hero">
          {prop.cover_url ? <img src={prop.cover_url} alt={prop.address}/> : <PropPlaceholder variant={0}/>}
        </div>
        <div className="listing-detail-grid">
          <div>
            <span className="eyebrow">{prop.tag || "In vendita"}</span>
            <h2 style={{fontSize:"clamp(1.7rem,3vw,2.4rem)",marginTop:"10px"}}>{prop.address}</h2>
            <p style={{color:"var(--muted)",fontSize:"1rem",marginTop:"6px"}}>{prop.city}</p>
            {prop.description && <p style={{fontSize:"1.05rem",lineHeight:1.7,marginTop:"32px",fontFamily:"'Bricolage Grotesque'",fontWeight:500}}>{prop.description}</p>}
          </div>
          <aside className="listing-info-panel" data-testid="cv-listing-info-panel">
            <div className="price">{priceFmt}</div>
            <div className="stats-list">
              {prop.surface_m2 ? <div><span className="k">Superficie</span>{prop.surface_m2} mq</div> : null}
              {prop.rooms ? <div><span className="k">Locali</span>{prop.rooms}</div> : null}
              {prop.bathrooms ? <div><span className="k">Bagni</span>{prop.bathrooms}</div> : null}
              {prop.city ? <div><span className="k">Zona</span>{prop.city}</div> : null}
            </div>
            <h3 style={{fontSize:"1.05rem",marginBottom:"18px"}}>Richiedi info</h3>
            <ContactFormCV slug={slug} propertyId={pid} testIdPrefix="cv-listing-contact" />
          </aside>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
//  Root
// ============================================================================
export default function CasaVicinaSite({ initialData, slug: slugProp }) {
  const location = useSiteLocation();
  const navigate = useSiteNavigate();
  // slug è passato dal wrapper PublicSite
  const slug = slugProp;
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (initialData || !slug) return;
    axios.get(`${API}/public/site/${slug}`)
      .then((r) => setData(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug, initialData]);

  const subPath = location.pathname.replace(`/site/${slug}`, "").replace(/^\//, "");
  const [seg1, seg2] = subPath.split("/");

  if (loading) return <div className="casa-vicina-site"><style>{CV_CSS}</style><div className="empty-state" style={{padding:"120px 20px"}}>Caricamento…</div></div>;
  if (notFound || !data) return (
    <div className="casa-vicina-site" data-testid="cv-not-found"><style>{CV_CSS}</style>
      <div className="empty-state" style={{padding:"120px 20px"}}>Sito non trovato. <button onClick={() => navigate("/")} style={{background:"none",border:"none",color:"var(--coral-dark)",cursor:"pointer",textDecoration:"underline"}}>Torna alla homepage</button></div>
    </div>
  );

  const { studio, featured_properties: properties } = data;
  return (
    <div className="casa-vicina-site" data-testid="cv-site-root" data-slug={slug}>
      <style>{CV_CSS}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
      <link href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=Work+Sans:wght@400;500;600&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet"/>
      <CVHeader slug={slug} studio={studio} />
      {(!seg1) ? <CVHomePage slug={slug} studio={studio} properties={properties} />
        : seg1 === "annunci" && !seg2 ? <CVAnnunciPage slug={slug} />
        : seg1 === "annunci" && seg2 ? <CVListingDetailPage slug={slug} pid={seg2} />
        : seg1 === "chi-siamo" ? <CVChiSiamoPage slug={slug} studio={studio} />
        : seg1 === "contatti" ? <CVContattiPage slug={slug} studio={studio} />
        : <div className="empty-state" style={{padding:"120px 20px"}}>Pagina non trovata.</div>}
      <CVFooter slug={slug} studio={studio} />
    </div>
  );
}
